import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/hooks/useAuthApi';
import { AlertCircle, CheckCircle2, Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import AuthLayout from '@/components/auth/AuthLayout';
import { normalizeAuthErrorMessage } from '@/lib/authErrors';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

const SetPasswordPage = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { updatePasswordWithSupabaseToken, reinitializeAuth } = useAuth();

  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string>('');
  const [organizationName, setOrganizationName] = useState<string>('');
  const [fromInvitation, setFromInvitation] = useState<boolean>(false);

  useEffect(() => {
    // Get data from navigation state (from invitation flow)
    const state = location.state as {
      email?: string;
      organizationName?: string;
      fromInvitation?: boolean;
      accessToken?: string;
      refreshToken?: string;
    } | null;
    if (state) {
      setUserEmail(state.email || '');
      setOrganizationName(state.organizationName || '');
      setFromInvitation(state.fromInvitation || false);

      // Check if tokens are passed from invitation flow
      if (state.accessToken && state.refreshToken) {
        setAccessToken(state.accessToken);
        setRefreshToken(state.refreshToken);
        return; // Skip URL hash parsing if we have tokens from state
      }
    }

    // Parse URL fragments for Supabase auth tokens (fallback for direct access)
    const hashParams = new URLSearchParams(location.hash.slice(1));
    const access_token = hashParams.get('access_token');
    const refresh_token = hashParams.get('refresh_token');
    const error = hashParams.get('error');
    const error_description = hashParams.get('error_description');

    if (error) {
      setError(error_description || 'Invalid or expired link. Please try again.');
      return;
    }

    if (access_token && refresh_token) {
      setAccessToken(access_token);
      setRefreshToken(refresh_token);
    } else {
      setError('Invalid or missing authentication tokens. Please try again.');
    }
  }, [location.hash, location.state]);

  const validatePassword = (password: string): string[] => {
    const errors: string[] = [];
    if (password.length < 8) {
      errors.push('Password must be at least 8 characters long');
    }
    if (!/(?=.*[a-z])/.test(password)) {
      errors.push('Password must contain at least one lowercase letter');
    }
    if (!/(?=.*[A-Z])/.test(password)) {
      errors.push('Password must contain at least one uppercase letter');
    }
    if (!/(?=.*\d)/.test(password)) {
      errors.push('Password must contain at least one number');
    }
    if (!/(?=.*[@$!%*?&])/.test(password)) {
      errors.push('Password must contain at least one special character (@$!%*?&)');
    }
    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!accessToken || !refreshToken) {
      setError('Missing authentication tokens. Please try again.');
      return;
    }

    // Validate password strength
    const passwordErrors = validatePassword(password);
    if (passwordErrors.length > 0) {
      setError(passwordErrors.join('. '));
      return;
    }

    // Check if passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await updatePasswordWithSupabaseToken(accessToken!, password, refreshToken || undefined);
      setSuccess(true);

      toast({
        title: "Password Set Successfully",
        description: fromInvitation
          ? `Your password has been set! Welcome to ${organizationName}.`
          : "Your password has been updated successfully.",
      });

      // Navigate to the appropriate page after a brief delay
      setTimeout(() => {
        if (fromInvitation) {
          navigate('/organization-settings');
        } else {
          navigate('/');
        }
      }, 2000);

    } catch (error: any) {
      console.error('Password update error:', error);
      setError(error.message || 'Failed to set password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setIsGoogleLoading(true);

    try {
      const redirectUrl = `${window.location.origin}/auth/google/callback`;
      const response = await fetch(import.meta.env.VITE_API_BASE_URL + '/api/auth/google/url', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          redirect_url: redirectUrl,
          context: 'login',
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to get OAuth URL');
      }

      const { url: authUrl } = await response.json();

      const popup = window.open(
        authUrl,
        'google_signin',
        'width=500,height=600,scrollbars=yes,resizable=yes'
      );

      if (!popup) {
        throw new Error('Popup blocked. Please allow popups and try again.');
      }

      const checkClosed = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkClosed);
          setError('Google sign-in was cancelled.');
          setIsGoogleLoading(false);
        }
      }, 1000);

      const messageListener = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;

        if (event.data.type === 'GOOGLE_AUTH_SUCCESS') {
          clearInterval(checkClosed);
          popup.close();
          window.removeEventListener('message', messageListener);

          handleGoogleAuthSuccess(event.data.code, event.data.state, redirectUrl);
        } else if (event.data.type === 'GOOGLE_AUTH_ERROR') {
          clearInterval(checkClosed);
          popup.close();
          window.removeEventListener('message', messageListener);

          setError(event.data.error || 'Google sign-in failed.');
          setIsGoogleLoading(false);
        }
      };

      window.addEventListener('message', messageListener);

      setTimeout(() => {
        clearInterval(checkClosed);
        window.removeEventListener('message', messageListener);
        if (!popup.closed) {
          popup.close();
        }
        setError('Google sign-in timed out. Please try again.');
        setIsGoogleLoading(false);
      }, 300000);
    } catch (error: any) {
      console.error('Google sign-in error:', error);
      setError(normalizeAuthErrorMessage(error?.message, 'Could not sign in with Google. Please try again.'));
      setIsGoogleLoading(false);
    }
  };

  const handleGoogleAuthSuccess = async (authCode: string, state: string, redirectUri: string) => {
    try {
      const response = await fetch(import.meta.env.VITE_API_BASE_URL + '/api/auth/google/callback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code: authCode,
          state: state,
          context: 'login',
          redirect_uri: redirectUri,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to authenticate with Google');
      }

      if (data.access_token && data.user) {
        localStorage.setItem('hobbes_auth_token', data.access_token);
        await reinitializeAuth();

        if (fromInvitation) {
          navigate('/organization-settings');
        } else {
          navigate('/');
        }
      } else {
        throw new Error('Invalid authentication response');
      }
    } catch (error: any) {
      console.error('Google auth callback error:', error);
      setError(normalizeAuthErrorMessage(error?.message, 'Authentication failed. Please try again.'));
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const getPasswordStrength = (password: string): { score: number; label: string; color: string } => {
    const errors = validatePassword(password);
    const score = Math.max(0, 5 - errors.length);

    if (score === 0) return { score, label: 'Very Weak', color: 'text-red-400' };
    if (score === 1) return { score, label: 'Weak', color: 'text-red-300' };
    if (score === 2) return { score, label: 'Fair', color: 'text-yellow-400' };
    if (score === 3) return { score, label: 'Good', color: 'text-yellow-300' };
    if (score === 4) return { score, label: 'Strong', color: 'text-green-300' };
    return { score, label: 'Very Strong', color: 'text-green-400' };
  };

  const passwordStrength = getPasswordStrength(password);

  const authInputClass = 'h-10 rounded-[10px] border-white/10 bg-transparent text-white text-base tracking-[-0.16px] placeholder:text-[#b2b2b2] focus-visible:ring-white/30 focus-visible:ring-offset-0';

  if (success) {
    return (
      <AuthLayout
        title="Password Set Successfully!"
        pageTitle="Password Set Successfully"
        showTermsLinks={false}
      >
        <div className="text-center space-y-4">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", duration: 0.5 }}
            className="flex justify-center"
          >
            <CheckCircle2 className="h-16 w-16 text-green-400" />
          </motion.div>
          <p className="text-[#a4a4a4]">
            {fromInvitation
              ? `Welcome to ${organizationName}! You're being redirected...`
              : "Your password has been updated. You're being redirected..."}
          </p>
          <div className="flex justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-white/50" />
          </div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={fromInvitation ? 'Welcome! Set Your Password' : 'Set New Password'}
      pageTitle="Set Your Password"
      showTermsLinks={false}
    >
      <p className="text-center text-[#a4a4a4] text-sm -mt-2 mb-6 tracking-[-0.14px]">
        {fromInvitation
          ? `Complete your setup for ${organizationName} by creating a secure password.`
          : "Create a strong password for your account."}
      </p>

      <form onSubmit={handleSubmit} className="flex flex-col gap-6">
        {userEmail && (
          <div className="text-sm text-[#a4a4a4] text-center tracking-[-0.14px]">
            Setting password for: <span className="text-white font-medium">{userEmail}</span>
          </div>
        )}

        <AnimatePresence mode="wait">
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="bg-red-500/20 border border-red-500/50 text-red-200 p-3 rounded-xl flex items-start gap-2"
            >
              <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span className="text-sm">{error}</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* New Password field */}
        <div className="flex flex-col gap-2">
          <label htmlFor="password" className="text-sm text-[#a4a4a4] tracking-[-0.14px]">
            New Password
          </label>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Enter your new password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className={`${authInputClass} pr-12`}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 text-[#a4a4a4] hover:text-white hover:bg-transparent"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </Button>
          </div>
          {password && (
            <div className="mt-2">
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-white/10 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all duration-300 ${
                      passwordStrength.score === 0 ? 'bg-red-500 w-1/5' :
                      passwordStrength.score === 1 ? 'bg-red-400 w-2/5' :
                      passwordStrength.score === 2 ? 'bg-yellow-500 w-3/5' :
                      passwordStrength.score === 3 ? 'bg-yellow-400 w-4/5' :
                      passwordStrength.score === 4 ? 'bg-green-400 w-full' :
                      'bg-green-500 w-full'
                    }`}
                  />
                </div>
                <span className={`text-xs font-medium ${passwordStrength.color}`}>
                  {passwordStrength.label}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Confirm Password field */}
        <div className="flex flex-col gap-2">
          <label htmlFor="confirmPassword" className="text-sm text-[#a4a4a4] tracking-[-0.14px]">
            Confirm Password
          </label>
          <div className="relative">
            <Input
              id="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="Confirm your new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className={`${authInputClass} pr-12`}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 text-[#a4a4a4] hover:text-white hover:bg-transparent"
              aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
            >
              {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </Button>
          </div>
        </div>

        {/* Set Password button */}
        <Button
          type="submit"
          disabled={isLoading || isGoogleLoading || !password || !confirmPassword}
          className="w-full h-10 bg-white text-[#404040] font-medium rounded-[36px] hover:bg-white/90 text-base tracking-[-0.16px]"
        >
          {isLoading ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Setting Password...
            </span>
          ) : (
            'Set Password'
          )}
        </Button>

        {fromInvitation && (
          <>
            <div className="flex items-center gap-4">
              <div className="flex-1 h-px bg-white/10" />
              <span className="text-xs text-[#a4a4a4] tracking-[-0.12px]">Or continue with</span>
              <div className="flex-1 h-px bg-white/10" />
            </div>

            <Button
              type="button"
              variant="outline"
              onClick={handleGoogleSignIn}
              disabled={isLoading || isGoogleLoading}
              className="w-full h-10 bg-white/10 border-white/10 text-[#e6e5e5] font-medium rounded-[36px] hover:bg-white/20 hover:text-white text-base tracking-[-0.16px]"
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
                <path d="M1 1h22v22H1z" fill="none" />
              </svg>
              Google
            </Button>
          </>
        )}

        <div className="text-xs text-[#a4a4a4] text-center space-y-1 tracking-[-0.12px]">
          <p>Password requirements:</p>
          <ul className="text-left space-y-1 inline-block">
            <li>• At least 8 characters long</li>
            <li>• Contains uppercase and lowercase letters</li>
            <li>• Contains at least one number</li>
            <li>• Contains at least one special character</li>
          </ul>
        </div>
      </form>
    </AuthLayout>
  );
};

export default SetPasswordPage;
